// ═══════════════════════════════════════════════════════════
// BottomNav — Apple-inspired white theme
// 3 nav items + language toggle
// ═══════════════════════════════════════════════════════════

import { motion, AnimatePresence } from "framer-motion";
import { Home, Search, Building2, Globe } from "lucide-react";
import { useLang } from "@/lib/i18n";

export type ViewType = "home" | "search" | "browse" | "detail";

interface Props {
  view: ViewType;
  onNavigate: (v: ViewType) => void;
}

export default function BottomNav({ view, onNavigate }: Props) {
  const { t, toggleLang, lang } = useLang();
  const activeKey = view === "detail" ? "browse" : view;

  const items = [
    { key: "home" as ViewType, icon: Home, label: t.navHome },
    { key: "search" as ViewType, icon: Search, label: t.navSearch },
    { key: "browse" as ViewType, icon: Building2, label: t.navBrowse },
  ];

  return (
    <nav
      className="cgb-bottom-nav fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50
        border-t border-[rgba(15,23,42,0.06)]"
      style={{
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        paddingBottom: "env(safe-area-inset-bottom, 4px)",
      }}
    >
      <div className="flex justify-around items-center py-1.5">
        {items.map(({ key, icon: Icon, label }) => {
          const isActive = activeKey === key;
          return (
            <button
              key={key}
              onClick={() => onNavigate(key)}
              className="relative flex flex-col items-center gap-0.5 px-4 py-2 min-w-[58px]"
              style={{ touchAction: "manipulation" }}
            >
              <motion.div
                animate={{ color: isActive ? "#1A2747" : "#9CA3AF" }}
                transition={{ duration: 0.2 }}
              >
                <Icon size={19} strokeWidth={isActive ? 2.2 : 1.6} />
              </motion.div>
              <motion.span
                animate={{ color: isActive ? "#0F1A33" : "#6B7280" }}
                transition={{ duration: 0.2 }}
                className={`text-[10.5px] ${isActive ? "font-bold" : "font-medium"}`}
              >
                {label}
              </motion.span>
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    layoutId="nav-pill"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    exit={{ opacity: 0, scaleX: 0 }}
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-[#1A2747]"
                  />
                )}
              </AnimatePresence>
            </button>
          );
        })}

        {/* Language toggle */}
        <button
          onClick={toggleLang}
          className="relative flex flex-col items-center gap-0.5 px-4 py-2 min-w-[58px]"
          style={{ touchAction: "manipulation" }}
          aria-label={lang === "ar" ? "Switch to English" : "تبديل إلى العربية"}
        >
          <Globe size={19} strokeWidth={1.6} className="text-[#6B7280]" />
          <span className="text-[10.5px] font-bold text-[#0F1A33] font-mono-floor">
            {t.langSwitch}
          </span>
        </button>
      </div>
    </nav>
  );
}
